import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Briefcase, Target, Users, ExternalLink } from 'lucide-react';
import { api } from '../api';

const LEVELUP_HOME = 'https://skillupwithlevelup.com';

const LoginPage = ({ onLogin, isLocalLogin = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });
      api.setToken(response.access_token);
      onLogin(response.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSSOLogin = () => {
    const levelUpBase = process.env.REACT_APP_LEVELUP_URL || LEVELUP_HOME;
    const ssoProductionBase = (process.env.REACT_APP_SSO_PRODUCTION_URL || window.location.origin).replace(/\/$/, '');
    const currentOrigin = window.location.origin;
    const isRelayNeeded = currentOrigin !== ssoProductionBase;
    const redirectBack = isRelayNeeded
      ? `${ssoProductionBase}/authenticate-user?relay=${encodeURIComponent(currentOrigin)}`
      : `${ssoProductionBase}/authenticate-user`;
    window.location.href = `${levelUpBase}/login?redirect_back=${encodeURIComponent(redirectBack)}&source=siab_portal`;
  };

  const FeatureList = () => (
    <div className="space-y-4">
      {[
        { icon: Award,    text: 'Certification Ready - Enable Designation & Specialization growth' },
        { icon: Users,    text: 'Sales Ready - Effective customer engagement capabilities' },
        { icon: Briefcase,text: 'Tech Deal Ready - Deal execution impact' },
        { icon: Target,   text: 'Project Ready - Repeatable delivery excellence' },
      ].map(({ icon: Icon, text }) => (
        <div key={text} className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-levelup-accent/20 flex items-center justify-center flex-shrink-0">
            <Icon className="text-levelup-accent" size={20} />
          </div>
          <span className="text-gray-300">{text}</span>
        </div>
      ))}
    </div>
  );

  /* ── Shared header bar ─────────────────────────────────────────────────── */
  const PageHeader = () => (
    <header className="flex items-center px-6 py-3 border-b border-white/10 flex-shrink-0">
      <div className="flex items-center gap-3">
        <img src="/technofocus-white.png" alt="Technofocus"
          className="h-5 brightness-0 invert opacity-90" />
        <span className="text-white/40 font-light">|</span>
        <img src="/logo-levelup.png" alt="LevelUp" className="h-5 opacity-90" />
        <span className="text-white/40 font-light">|</span>
        <img src="/ms-levelup-logo.svg" alt="Sponsored by Microsoft" className="h-6 opacity-90" />
        <span className="text-white/40 font-light">|</span>
        <span className="text-white/90 font-semibold text-sm tracking-wide">
          Skilling In A Box
        </span>
      </div>
    </header>
  );

  /* ── Shared footer bar ─────────────────────────────────────────────────── */
  const PageFooter = () => (
    <footer className="flex items-center justify-between px-6 py-3 border-t border-white/10 flex-shrink-0">
      <div className="flex items-center gap-2 text-xs text-white/50">
        <span>Skilling In A Box</span>
        <span className="text-white/30">|</span>
        <span>LevelUp</span>
        <span className="text-white/30">|</span>
        <span>Sponsored by Microsoft</span>
      </div>
      <div className="flex items-center gap-5 text-xs">
        <a
          href={LEVELUP_HOME}
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/50 hover:text-white transition-colors flex items-center gap-1"
          data-testid="footer-levelup-home"
        >
          LevelUp Home <ExternalLink size={11} />
        </a>
        <a
          href="https://technofocus.ai/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/50 hover:text-white transition-colors"
          data-testid="footer-privacy"
        >
          Privacy Statement
        </a>
      </div>
    </footer>
  );

  // ── LOCAL LOGIN (backup admin form) ──────────────────────────────────────
  if (isLocalLogin) {
    return (
      <div className="min-h-screen gradient-hero flex flex-col">
        <PageHeader />
        <div className="flex flex-1">
          <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 py-8">
            <h1 className="text-4xl font-bold text-white mb-4">
              Skilling in a Box
              <sup className="text-lg font-semibold text-levelup-accent ml-1 align-super">Beta</sup>
            </h1>
            <p className="text-xl text-gray-300 mb-8">Enabling partners to scale skilling with velocity.</p>
            <FeatureList />
          </div>

          <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-levelup-dark">Administrator Sign In</h2>
                <p className="text-gray-400 text-sm mt-1">Restricted — for admin accounts only</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
              )}

              <form onSubmit={handleLocalLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" required className="input-field" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com" data-testid="email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input type="password" required className="input-field" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••" data-testid="password-input" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-levelup-dark text-white px-6 py-3 rounded-lg font-semibold hover:bg-levelup-darker transition-all disabled:opacity-50"
                  data-testid="submit-button">
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>

              <div className="text-center mt-6">
                <a href="/" className="text-sm text-levelup-dark font-medium hover:underline transition-colors">
                  ← Sign in with LevelUp
                </a>
              </div>
            </div>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  // ── DEFAULT SSO-ONLY LOGIN ────────────────────────────────────────────────
  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      <PageHeader />

      <div className="flex flex-1">
        {/* Left — feature list */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 py-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Skilling in a Box
            <sup className="text-lg font-semibold text-levelup-accent ml-1 align-super">Beta</sup>
          </h1>
          <p className="text-xl text-gray-300 mb-8">Enabling partners to scale skilling with velocity.</p>
          <FeatureList />
        </div>

        {/* Right — SSO card */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
            {/* Mobile logo */}
            <div className="text-center mb-6 lg:hidden">
              <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                <img src="/technofocus-white.png" alt="Technofocus" className="h-5 brightness-0" />
                <span className="text-gray-400">|</span>
                <img src="/logo-levelup-dark.png" alt="LevelUp" className="h-5" />
              </div>
              <h1 className="text-xl font-bold text-levelup-dark">Skilling in a Box
                <sup className="text-xs font-semibold text-levelup-accent ml-1 align-super">Beta</sup>
              </h1>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-levelup-dark">Welcome Back</h2>
              <p className="text-gray-500 text-sm mt-1">Sign in to access your training content</p>
            </div>

            {/* Primary CTA */}
            <button onClick={handleSSOLogin}
              className="w-full flex items-center justify-center gap-3 bg-levelup-dark text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-levelup-darker transition-all"
              data-testid="levelup-sso-btn">
              <img src="/logo-levelup.png" alt="" className="h-5 brightness-0 invert" />
              Sign in with LevelUp
            </button>

            {/* Sign-out hint */}
            <p className="text-center text-xs text-gray-400 mt-4 leading-relaxed" data-testid="signout-hint">
              To logout and sign in with a different account, logout of{' '}
              <a
                href={LEVELUP_HOME}
                target="_blank"
                rel="noopener noreferrer"
                className="text-levelup-teal hover:underline font-medium"
              >
                LevelUp
              </a>
              {' '}as well.
            </p>

            {/* /local-login is still accessible directly as a hidden backup route */}
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};

export default LoginPage;
